import {
  Button,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SMMHubLogo } from 'components/SMMHubLogo';
import { useContext, useEffect, useRef, useState } from 'react';
import { ReactComponent as CloseIcon } from 'assets/images/close-icon.svg';
import { EMAIL_REGEX } from 'utils/constants';
import { useNavigate } from 'react-router-dom';
import { postLoginEmail, postLoginEmailCode } from 'api/api';
import { AuthCookieContext } from 'utils/context';
import { Header } from 'components/Header';
import { useTranslation } from 'react-i18next';

const steps = [
  {
    title: 'loginPage.enter_email_to_login',
    placeholder: 'loginPage.enter_email',
    button: 'loginPage.sign_in',
  },
  {
    title: 'loginPage.enter_code',
    placeholder: 'loginPage.enter_code_placeholder',
    button: 'loginPage.lets_go',
  },
];

const LoginPage = () => {
  const { t } = useTranslation();

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(60);
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { loadUserInfo } = useContext(AuthCookieContext);

  const intervalRef = useRef();

  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    step === 0 ? setEmail(e.target.value) : setCode(e.target.value);
    setError('');
  };

  const handleClearInput = () => {
    step === 0 ? setEmail('') : setCode('');
  };

  const sentEmail = async () => {
    try {
      setIsLoading(true);
      const res = await postLoginEmail({
        email,
      });
      setPayload(res.payload);
      setStep(1);
    } catch (error) {
      console.error(error);
      if (error.status >= 400 && error.status < 500) {
        setError(t('loginPage.user_not_found'));
      } else {
        setError('loginPage.something_went_wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendCode = async () => {
    try {
      setIsLoading(true);
      const res = await postLoginEmailCode({
        code,
        payload,
      });
      if (res.success) {
        await loadUserInfo();
        navigate('/account');
      } else {
        setError(res.error);
      }
    } catch (error) {
      console.error(error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 0) {
      if (EMAIL_REGEX.test(email)) {
        await sentEmail();
      } else {
        setError('loginPage.enter_valid_email');
      }
    } else {
      if (code) {
        await sendCode();
      } else {
        setError('loginPage.enter_code_error');
      }
    }
  };

  const handleResendCode = () => {
    console.log('resend code');
    setTimer(60);
    startTimer();
  };

  const startTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev === 0) {
          clearInterval(intervalRef.current);
          return 0;
        } else {
          return prev - 1;
        }
      });
    }, 1000);
  };

  useEffect(() => {
    if (step === 1) {
      setTimer(60);
      startTimer();
      return () => clearInterval(intervalRef.current);
    }
  }, [step]);

  return (
    <Container maxWidth="xs">
      <Header
        title={<SMMHubLogo />}
        withoutMenu
        onNavClick={step === 1 ? () => setStep(0) : undefined}
      />
      <Stack gap={5} mt={8}>
        <Typography variant="h1">{t('loginPage.sign_in_to_smmhub')}</Typography>
        <Stack gap={4.25}>
          <Typography textAlign={'center'}>
            {t(steps[step].title) + (step !== 0 ? email : '')}
          </Typography>
          <Stack gap={1.5} component={'form'} onSubmit={handleSubmit}>
            <TextField
              type={step === 0 ? 'email' : 'text'}
              value={step === 0 ? email : code}
              onChange={handleEmailChange}
              placeholder={t(steps[step].placeholder)}
              error={!!error}
              helperText={t(error)}
              disabled={isLoading}
              InputProps={{
                endAdornment: email ? (
                  <InputAdornment>
                    <IconButton disabled={isLoading} onClick={handleClearInput}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Button type="submit">{t(steps[step].button)}</Button>
          </Stack>
          {!!step && (
            <Typography
              sx={{
                fontSize: '12px',
                textAlign: 'center',
                color: '#808396',
              }}
            >
              {timer !== 0 ? (
                <>
                  {t('loginPage.code_has_been_sent')}
                  <br />
                  {t('loginPage.resend_time', { time: timer })}
                </>
              ) : (
                <Button
                  variant="text"
                  sx={{
                    p: 0.5,
                    fontWeight: 500,
                    textDecoration: 'underline',
                  }}
                  onClick={handleResendCode}
                >
                  {t('loginPage.resend_code')}
                </Button>
              )}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};

export default LoginPage;
